import type { EntitySnapshot } from './protocol';

/**
 * Snapshot buffering and entity interpolation.
 *
 * The server simulates at 30 Hz; the client renders at whatever the display can
 * do. Rendering the newest snapshot directly makes everything step 30 times a
 * second, and exponentially smoothing toward it — which this replaced — is a
 * low-pass filter that permanently trails the truth and never arrives.
 *
 * The correct answer is to render slightly in the past. Hold a short buffer of
 * snapshots and draw the world as it was at `now - INTERP_DELAY_MS`, lerping
 * between the two snapshots that bracket that instant. Every frame then has two
 * *known* states to sit between, so motion is exact rather than approximated, and
 * a late packet costs smoothness instead of correctness.
 *
 * Pure: no Babylon, no clock of its own. The caller supplies the time.
 */

/**
 * How far behind live the world is drawn, in milliseconds.
 *
 * Two server ticks. One would leave no slack — a single packet arriving a
 * millisecond late would starve the buffer and stall. Two buys a whole tick of
 * jitter tolerance for 66 ms of added latency, which is invisible for
 * click-to-move and is the standard trade.
 */
export const INTERP_DELAY_MS = 66;

/** Snapshots retained. About half a second at 30 Hz — enough to ride out a stall. */
const BUFFER_SIZE = 16;

export interface BufferedSnapshot {
  /** Client receive time, from performance.now(). */
  receivedAt: number;
  tick: number;
  entities: Map<string, EntitySnapshot>;
}

/** An entity's interpolated state for one frame. */
export interface InterpolatedEntity {
  x: number;
  z: number;
  facing: number;
}

/**
 * A rolling window of server snapshots.
 *
 * Deliberately not reactive and not in the store: this is read every frame by the
 * render loop, and putting it in the reactive graph would schedule DOM work 144
 * times a second.
 */
export class SnapshotBuffer {
  private readonly buffer: BufferedSnapshot[] = [];

  /** Adds a snapshot, stamped with the time it arrived. */
  push(tick: number, entities: readonly EntitySnapshot[], receivedAt: number): void {
    const map = new Map<string, EntitySnapshot>();
    for (const e of entities) map.set(e.id, e);

    this.buffer.push({ receivedAt, tick, entities: map });
    // Out-of-order arrival is possible on a lossy link; keep the window sorted so
    // the bracketing search stays valid.
    if (this.buffer.length > 1 && receivedAt < (this.buffer[this.buffer.length - 2]?.receivedAt ?? 0)) {
      this.buffer.sort((a, b) => a.receivedAt - b.receivedAt);
    }
    while (this.buffer.length > BUFFER_SIZE) this.buffer.shift();
  }

  /** Everything currently held, oldest first. */
  get snapshots(): readonly BufferedSnapshot[] {
    return this.buffer;
  }

  /** Drops everything. Used on a zone change, where the old positions are lies. */
  clear(): void {
    this.buffer.length = 0;
  }

  /**
   * The interpolated state of one entity at the render instant, or undefined if
   * the buffer knows nothing about it.
   */
  sample(id: string, now: number): InterpolatedEntity | undefined {
    const target = now - INTERP_DELAY_MS;
    const pair = this.bracket(target);
    if (!pair) return undefined;

    const [prev, next, alpha] = pair;
    const a = prev.entities.get(id);
    const b = next.entities.get(id);

    // Present in only one snapshot means it just spawned or just died; use
    // whichever exists rather than interpolating from nothing.
    if (!a) return b ? { x: b.x, z: b.z, facing: b.facing } : undefined;
    if (!b) return { x: a.x, z: a.z, facing: a.facing };

    return {
      x: a.x + (b.x - a.x) * alpha,
      z: a.z + (b.z - a.z) * alpha,
      facing: lerpAngleShortest(a.facing, b.facing, alpha),
    };
  }

  /**
   * The newest snapshot's view of an entity — the authoritative truth, without
   * interpolation. Used for reconciliation and for anything that must not lag,
   * like health.
   */
  latest(id: string): EntitySnapshot | undefined {
    return this.buffer[this.buffer.length - 1]?.entities.get(id);
  }

  /**
   * Finds the two snapshots surrounding a render instant and the fraction
   * between them.
   *
   * Clamps rather than extrapolating at both ends: guessing ahead of the newest
   * snapshot makes entities overshoot and then visibly snap back, which reads far
   * worse than a brief pause.
   */
  private bracket(target: number): [BufferedSnapshot, BufferedSnapshot, number] | null {
    if (this.buffer.length === 0) return null;
    if (this.buffer.length === 1) {
      const only = this.buffer[0] as BufferedSnapshot;
      return [only, only, 0];
    }

    const oldest = this.buffer[0] as BufferedSnapshot;
    const newest = this.buffer[this.buffer.length - 1] as BufferedSnapshot;

    // Buffer has not filled yet, or a long stall: hold at the nearest end.
    if (target <= oldest.receivedAt) return [oldest, oldest, 0];
    if (target >= newest.receivedAt) return [newest, newest, 0];

    for (let i = 0; i < this.buffer.length - 1; i++) {
      const prev = this.buffer[i] as BufferedSnapshot;
      const next = this.buffer[i + 1] as BufferedSnapshot;
      if (target >= prev.receivedAt && target <= next.receivedAt) {
        const span = next.receivedAt - prev.receivedAt;
        // Two snapshots stamped identically would divide by zero.
        const alpha = span > 0 ? (target - prev.receivedAt) / span : 0;
        return [prev, next, alpha];
      }
    }

    return [newest, newest, 0];
  }
}

/**
 * Interpolates an angle the short way round, so a heading crossing ±π does not
 * unwind a full turn.
 */
export function lerpAngleShortest(from: number, to: number, t: number): number {
  const TAU = Math.PI * 2;
  let delta = (to - from) % TAU;
  if (delta > Math.PI) delta -= TAU;
  if (delta < -Math.PI) delta += TAU;
  return from + delta * Math.min(Math.max(t, 0), 1);
}
